"use client";
import { AlgoliaProvider } from "@components/algolia/AlgoliaProvider";
import { HeroPLP } from "@components/cms/HeroPLP/HeroPLP";
import { RichText } from "@components/cms/rich-text-renderer/RichText";
import { Banner } from "@components/commerce/categories/banner";
import { ProductListing } from "@components/commerce/product-listing/ProductListing";
import { FiltersController } from "@components/commerce/product-listing/filters/context/FiltersContextProvider";
import type { IModuleHero, IPageProductListingPage } from "@contentful-api/types/contentful";
import { CommonCMS } from "@lib/constants/contentful";
import { useMicrocopy } from "@lib/contentful/microcopy/MicrocopyContext";
import { getAdditionalSizesFilters } from "@lib/utils/plp/plpShoeSizes";
import { useFilterStore, useStore } from "@store/index";
import { PageHeading } from "@ui/components/content/page-heading/PageHeading";
import { Box } from "@ui/components/layout/box/Box";
import { isEqual } from "instantsearch.js/es/lib/utils";
import { useLocale } from "next-intl";
import { useEffect } from "react";
import { ProductBreadcrumbs } from "@components/commerce/product/product-breadcrumbs/ProductBreadcrumbs";
import { notFound, usePathname } from "next/navigation";
import BazaarvoiceGalleryContainer from "@components/cms/bazaarvoice-gallery-container/BazaarvoiceGalleryContainer";

type ProductListingPageProps = {
    page: IPageProductListingPage;
    isMenPage: boolean;
    // TODO: Add typing of initialFiltersList
    initialFiltersList: Record<string, any[]>;
    // TODO: Add typing of algoliaFallbackState
    algoliaFallbackState?: Record<string, unknown>;
};

export const ProductListingPage = ({
    page,
    isMenPage,
    initialFiltersList,
    algoliaFallbackState = {},
}: ProductListingPageProps) => {
    const locale = useLocale();
    const pathname = usePathname();

    const { getMultiple } = useMicrocopy();

    const microcopies = getMultiple(CommonCMS.shared, [
        CommonCMS.generic.readMore,
        CommonCMS.generic.readLess,
    ]);

    const setInitialFilters = useStore((state) => state.setInitialFilters);
    const initialFilters = useStore((state) => state.data.products.initialFilters);
    const productResultsCount = useFilterStore((state) => state.resultCount);
    const lastLoadedPage = useStore((state) => state.data.products.lastLoadedPage);

    useEffect(() => {
        if (!isEqual(initialFilters, initialFiltersList)) {
            setInitialFilters(initialFiltersList);
        }
    }, [page]);

    if (!page?.fields) {
        return notFound();
    }

    const fields = page.fields;
    const {
        title,
        desc,
        plpHeroImage,
        filtersList = [],
        widgetSize = "Small",
        titleSize = "Extra Large",
        descriptionSize = "Normal",
        editorialWidgets,
        overlay,
        legalText,
        colorText,
        callToActions,
        sortByOptionsList = [],
        bazaarvoiceGallery,
        categoriesBanner,
    } = fields ?? {};

    const bannerItems = categoriesBanner?.fields?.links ?? [];

    const listOfFilters = [...filtersList, ...getAdditionalSizesFilters(filtersList)];

    const cleanedPathname = pathname.replace(/^\/[a-z]{2}-[A-Z]{2}/, "");
    const pageCategory = decodeURIComponent(
        cleanedPathname.slice(
            1,
            cleanedPathname.indexOf("?") === -1 ? undefined : cleanedPathname.indexOf("?")
        )
    ).replaceAll("/", ">");

    return (
        <>
            <ProductBreadcrumbs
                items={
                    decodeURIComponent(cleanedPathname)
                        ?.split("?")[0]
                        ?.split("/")
                        .filter((it) => !!it) || []
                }
                locale={locale}
                marginY={6}
                pl={[3, 3, 4, 6]}
                zIndex={3}
            />
            <main>
                {plpHeroImage ? (
                    <Box position={"relative"} mb="4">
                        <HeroPLP
                            module={
                                {
                                    fields: {
                                        title,
                                        description: <RichText data={desc} />,
                                        image: plpHeroImage,
                                        resultsCount: productResultsCount,
                                        imageSize: "Large",
                                        theme: "Dark",
                                        legalText,
                                        callToActions,
                                        backgroundColor: { value: "white" },
                                    },
                                } as unknown as IModuleHero
                            }
                            color={colorText}
                            index={0}
                            overlay={overlay}
                        />
                    </Box>
                ) : (
                    <PageHeading
                        title={title}
                        description={<RichText data={desc} />}
                        showMoreLabel={microcopies[CommonCMS.generic.readMore]}
                        showLessLabel={microcopies[CommonCMS.generic.readLess]}
                        titleSize={titleSize}
                        descriptionSize={descriptionSize}
                        resultsCount={productResultsCount}
                    />
                )}
                <Box ml={[3, 3, 4, 6]}>
                    <Banner bannerItems={bannerItems} />
                </Box>
                <AlgoliaProvider
                    locale={locale}
                    pagePath={pathname}
                    algoliaFallbackState={algoliaFallbackState}
                >
                    <FiltersController
                        listOfFilters={listOfFilters}
                        sortByOptionsList={sortByOptionsList}
                        isMenPage={isMenPage}
                        pageCategory={pageCategory}
                        page={lastLoadedPage}
                    >
                        <ProductListing
                            listOfFilters={listOfFilters}
                            locale={locale}
                            widgetSize={widgetSize}
                            editorialWidgets={editorialWidgets}
                        />
                    </FiltersController>
                </AlgoliaProvider>
                {bazaarvoiceGallery && <BazaarvoiceGalleryContainer module={bazaarvoiceGallery} />}
            </main>
        </>
    );
};
